"use client";

import { useMemo, useState } from "react";
import {
  Button,
  Icon,
  Input,
  Label,
  Layers,
  Lock,
  Search,
  SegmentedControl,
  Select,
  Slider,
  Swatch,
  Tooltip,
  cx,
} from "@/components";
import { getMapTokens, type MapSpec } from "@/lib/map";
import { overlayGate, type OverlayEntitlement } from "@/lib/overlay/gate";
import { applyOverlaySpec, buildOverlaySpec } from "@/lib/overlay/apply";
import { DEFAULT_CLASS_COUNT } from "@/lib/overlay/classify";
import { RAMP_FAMILIES } from "@/lib/overlay/ramp";
import type { OverlaySpec } from "@/lib/overlay/spec";
import type { Choropleth, ClassificationMethod, DemographicVariable } from "@/lib/overlay/types";
import type { BoundaryAggregate } from "@/lib/overlay/geoJoin";
import type { OverlayCatalogGroup } from "@/lib/overlay/adapters";
import { overlayLegendRows } from "./overlayLayers";

export interface OverlayPanelProps {
  spec: MapSpec;
  themeName: string;
  idPrefix: string;
  /** White-label brand name for the (otherwise brand-free) locked copy. */
  brandName: string;
  /** Build-go flag (`brand.demographicsOverlays`). */
  demographicsOverlays: boolean;
  /** True only when a deploy-time overlay render engine is bound (co-gate). */
  engineAvailable: boolean;
  /** Tier-derived entitlement (never usage-derived). */
  entitlement: OverlayEntitlement;
  /** Static variable catalog grouped by source/country (from `overlayCatalog()`). */
  catalog: OverlayCatalogGroup[];
  onSpecChange: (next: MapSpec) => void;
  /**
   * Resolved choropleth for the legend (deploy-time data via the overlay engine).
   * Null → the legend shows a "renders once data is loaded" note.
   */
  choropleth?: Choropleth | null;
  /** Per-boundary geo-join rollup of the user's own rows (on boundary click). */
  rollup?: BoundaryAggregate | null;
  /** Self-serve upgrade link for the Free-tier locked state. */
  upgradeHref?: string;
}

const RAMP_OPTIONS: Array<{ value: string; label: string }> = [
  { value: RAMP_FAMILIES.sequential, label: "Sequential" },
  { value: RAMP_FAMILIES.diverging, label: "Diverging" },
  { value: RAMP_FAMILIES.heat, label: "Heat" },
];

const METHOD_OPTIONS: Array<{ value: ClassificationMethod; label: string }> = [
  { value: "quantile", label: "Quantile" },
  { value: "equal", label: "Equal" },
  { value: "manual", label: "Manual" },
];

/** Parse a "0, 100, 200" breaks string → ascending numbers, or null when invalid. */
function parseBreaks(text: string): number[] | null {
  const nums = text
    .split(/[,\s]+/)
    .map((t) => t.trim())
    .filter((t) => t !== "")
    .map(Number);
  if (nums.length < 2 || nums.some((n) => !Number.isFinite(n))) return null;
  for (let i = 1; i < nums.length; i++) if (nums[i]! <= nums[i - 1]!) return null;
  return nums;
}

/** Compact numeric formatter for the legend edges. */
function fmt(n: number): string {
  if (!Number.isFinite(n)) return "—";
  if (Math.abs(n) >= 1000) return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

/**
 * The M9-B demographics/census overlay panel — one token-only surface, gated via
 * `overlayGate`. Free tier sees a self-serve upsell (no dark pattern); an
 * entitled plan gets the variable picker (search + browse the catalog grouped by
 * source/country, labels + units), a boundary-tier selector, the
 * classification + manual-breaks control, ramp + opacity, the choropleth legend
 * (token-keyed swatches, instant custom tooltip), and a per-boundary user-data
 * rollup. Every selection persists into `spec.overlay` via the M9-A engine hooks.
 */
export function OverlayPanel({
  spec,
  themeName,
  idPrefix,
  brandName,
  demographicsOverlays,
  engineAvailable,
  entitlement,
  catalog,
  onSpecChange,
  choropleth = null,
  rollup = null,
  upgradeHref = "/pricing",
}: OverlayPanelProps) {
  const tokens = useMemo(() => getMapTokens(themeName), [themeName]);
  const gate = overlayGate(demographicsOverlays, entitlement, engineAvailable);
  const overlay = spec.overlay;

  const [search, setSearch] = useState("");
  const [manualText, setManualText] = useState((overlay?.classification.breaks ?? []).join(", "));
  const [note, setNote] = useState<string | null>(null);

  // Resync the manual-breaks draft when the selected variable/method changes (a
  // load or a fresh pick) — the React "adjust state during render on a key
  // change" pattern (no effect), so user edits between syncs are never clobbered.
  const syncKey = overlay ? `${overlay.variableId}:${overlay.classification.method}` : "";
  const [syncedKey, setSyncedKey] = useState(syncKey);
  if (syncKey !== syncedKey) {
    setSyncedKey(syncKey);
    setManualText((overlay?.classification.breaks ?? []).join(", "));
    setNote(null);
  }

  if (!gate.present) return null;

  function write(next: OverlaySpec | undefined) {
    onSpecChange(applyOverlaySpec(spec, next));
  }
  function patch(p: Partial<OverlaySpec>) {
    if (!overlay) return;
    write({ ...overlay, ...p });
  }

  /* -------------------------------------------------- Free-tier locked seam */
  if (gate.locked) {
    return (
      <div className="overlay-panel__body">
        <div className="overlay-locked">
          <span className="overlay-tool__head">
            <Icon icon={Lock} size="sm" />
            <span className="overlay-tool__title">Demographics &amp; census overlays</span>
          </span>
          <p className="overlay-tool__hint">
            Shade your map by population, income, age, housing, and more — public census data joined to
            your points. Available on a {brandName} paid plan.
          </p>
          <a className="overlay-upgrade" href={upgradeHref}>
            Upgrade your plan
          </a>
          <p className="overlay-tool__note">Cancel anytime — your maps stay exactly as you left them.</p>
        </div>
      </div>
    );
  }

  /* ----------------------------------------------------------- variable pick */
  const group = overlay ? catalog.find((g) => g.source === overlay.source && g.country === overlay.country) : undefined;
  const selectedVar = group?.variables.find((v) => v.id === overlay?.variableId);

  const query = search.trim().toLowerCase();
  const filteredGroups = catalog
    .map((g) => ({
      group: g,
      variables:
        query === ""
          ? g.variables
          : g.variables.filter(
              (v) => v.label.toLowerCase().includes(query) || v.id.toLowerCase().includes(query) || v.units.toLowerCase().includes(query),
            ),
    }))
    .filter((g) => g.variables.length > 0);

  function selectVariable(g: OverlayCatalogGroup, v: DemographicVariable) {
    const tier = overlay?.tier && g.tiers.includes(overlay.tier) ? overlay.tier : g.tiers[0]!;
    write(
      buildOverlaySpec({
        source: g.source,
        country: g.country,
        variableId: v.id,
        tier,
        ...(overlay ? { classification: overlay.classification, rampTokenId: overlay.rampTokenId, opacity: overlay.opacity } : {}),
      }),
    );
  }

  function changeMethod(m: ClassificationMethod) {
    if (!overlay) return;
    if (m === "manual") {
      const breaks = parseBreaks(manualText) ?? choropleth?.breaks ?? null;
      if (!breaks || breaks.length < 2) {
        setNote("Enter at least two ascending break values to use manual classification.");
        return;
      }
      setManualText(breaks.join(", "));
      setNote(null);
      patch({ classification: { method: "manual", breaks } });
    } else {
      setNote(null);
      patch({ classification: { method: m, classes: overlay.classification.classes ?? DEFAULT_CLASS_COUNT } });
    }
  }

  function changeClasses(n: number) {
    if (!overlay || overlay.classification.method === "manual") return;
    patch({ classification: { method: overlay.classification.method, classes: Math.max(2, n) } });
  }

  function changeManual(text: string) {
    setManualText(text);
    if (!overlay) return;
    const breaks = parseBreaks(text);
    if (!breaks) {
      setNote("Enter at least two ascending break values.");
      return;
    }
    setNote(null);
    patch({ classification: { method: "manual", breaks } });
  }

  const legendRows = choropleth ? overlayLegendRows(choropleth, tokens) : [];
  const opacityPct = Math.round((overlay?.opacity ?? 0) * 100);

  return (
    <div className="overlay-panel__body">
      {/* ----------------------------------------------- variable picker */}
      <div className="overlay-tool">
        <span className="overlay-tool__head">
          <Icon icon={Layers} size="sm" />
          <span className="overlay-tool__title">Census variable</span>
          {overlay && (
            <Button variant="ghost" size="sm" className="overlay-tool__clear" onClick={() => write(undefined)}>
              Remove overlay
            </Button>
          )}
        </span>
        {selectedVar ? (
          <p className="overlay-tool__selected">
            <span className="overlay-tool__selected-label">{selectedVar.label}</span>
            <span className="overlay-tool__units">{selectedVar.units}</span>
          </p>
        ) : (
          <p className="overlay-tool__hint">Search or browse public census variables to shade your map.</p>
        )}
        <div className="overlay-search">
          <Icon icon={Search} size="sm" className="overlay-search__glyph" />
          <Input
            id={`${idPrefix}-overlay-search`}
            placeholder="Search variables…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search census variables"
          />
        </div>
        <div className="overlay-catalog" role="listbox" aria-label="Census variables">
          {filteredGroups.map(({ group: g, variables }) => (
            <div key={`${g.source}-${g.country}`} className="overlay-catalog__group">
              <p className="overlay-catalog__grouphead">
                {g.country} · {g.source.replace(/_/g, " ")}
              </p>
              {variables.map((v) => {
                const active = overlay?.variableId === v.id && overlay?.source === g.source;
                return (
                  <button
                    key={v.id}
                    type="button"
                    role="option"
                    aria-selected={active}
                    className={cx("overlay-catalog__item", active && "overlay-catalog__item--active")}
                    onClick={() => selectVariable(g, v)}
                  >
                    <span className="overlay-catalog__label">{v.label}</span>
                    <span className="overlay-catalog__units">{v.units}</span>
                  </button>
                );
              })}
            </div>
          ))}
          {filteredGroups.length === 0 && <p className="overlay-tool__note">No variables match “{search}”.</p>}
        </div>
      </div>

      {/* ----------------------------------------------- controls (once picked) */}
      {overlay && group && (
        <>
          <div className="overlay-tool">
            <span className="overlay-tool__title">Boundary tier</span>
            <Select
              id={`${idPrefix}-overlay-tier`}
              value={overlay.tier}
              onChange={(e) => patch({ tier: e.target.value })}
              aria-label="Boundary tier"
            >
              {group.tiers.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
          </div>

          <div className="overlay-tool">
            <span className="overlay-tool__title">Classification</span>
            <SegmentedControl
              label="Classification method"
              value={overlay.classification.method}
              onChange={(m) => changeMethod(m as ClassificationMethod)}
              options={METHOD_OPTIONS}
            />
            {overlay.classification.method === "manual" ? (
              <div className="overlay-tool__row">
                <Label htmlFor={`${idPrefix}-overlay-breaks`}>Break values</Label>
                <Input
                  id={`${idPrefix}-overlay-breaks`}
                  value={manualText}
                  placeholder="e.g. 0, 100, 250, 500"
                  onChange={(e) => changeManual(e.target.value)}
                />
              </div>
            ) : (
              <div className="overlay-tool__row">
                <Label htmlFor={`${idPrefix}-overlay-classes`}>Classes</Label>
                <Input
                  id={`${idPrefix}-overlay-classes`}
                  type="number"
                  min={2}
                  max={9}
                  value={String(overlay.classification.classes ?? DEFAULT_CLASS_COUNT)}
                  onChange={(e) => changeClasses(Number(e.target.value) || DEFAULT_CLASS_COUNT)}
                />
              </div>
            )}
            {note && (
              <p className="overlay-tool__note" role="status">
                {note}
              </p>
            )}
          </div>

          <div className="overlay-tool">
            <span className="overlay-tool__title">Color ramp</span>
            <Select
              id={`${idPrefix}-overlay-ramp`}
              value={overlay.rampTokenId}
              onChange={(e) => patch({ rampTokenId: e.target.value })}
              aria-label="Color ramp"
            >
              {RAMP_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </Select>
          </div>

          <div className="overlay-tool">
            <span className="overlay-tool__title">Layer opacity</span>
            <Slider
              min={0}
              max={1}
              step={0.05}
              value={overlay.opacity}
              showValue
              formatValue={() => `${opacityPct}%`}
              onChange={(e) => patch({ opacity: Number(e.target.value) })}
              aria-label="Overlay opacity"
            />
          </div>

          {/* ------------------------------------------------------- legend */}
          <div className="overlay-legend">
            <span className="overlay-legend__head">
              <span className="overlay-tool__title">Legend</span>
              {selectedVar && (
                <Tooltip content={selectedVar.sourceCitation} position="above">
                  <span className="overlay-legend__units">{selectedVar.units}</span>
                </Tooltip>
              )}
            </span>
            {legendRows.length > 0 ? (
              <ul className="overlay-legend__rows">
                {legendRows.map((row, i) => (
                  <li key={i} className="overlay-legend__row">
                    <Swatch color={row.color} />
                    <span className="overlay-legend__range">
                      {fmt(row.from)}
                      {row.to !== null ? ` – ${fmt(row.to)}` : "+"}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="overlay-tool__note">The legend renders once boundary data is loaded for this variable.</p>
            )}
          </div>
        </>
      )}

      {/* ------------------------------------------- per-boundary rollup popover */}
      {rollup && (
        <div className="overlay-rollup" role="dialog" aria-label="Boundary rollup">
          <span className="overlay-tool__head">
            <span className="overlay-tool__title">{rollup.name ?? rollup.boundaryId}</span>
          </span>
          <p className="overlay-rollup__count">
            {rollup.count.toLocaleString()} of your points here
          </p>
          <ul className="overlay-rollup__cols">
            {Object.entries(rollup.columns).map(([col, agg]) => (
              <li key={col} className="overlay-rollup__col">
                <span className="overlay-rollup__colname">{col}</span>
                <span className="overlay-rollup__colval">
                  Σ {fmt(agg.sum)} · μ {fmt(agg.mean)}
                </span>
              </li>
            ))}
          </ul>
          <p className="overlay-tool__note">
            Mean geocode confidence {Math.round(rollup.meanConfidence * 100)}%
          </p>
        </div>
      )}
    </div>
  );
}
